import { v4 as uuidv4 } from 'uuid'
import type { PostgrestError } from '@supabase/supabase-js'

import { GEOXP_SPOTS_TABLE, SPOT_LOCALIZATIONS_TABLE } from '../constants/db.constants'
import {
  dbUpsert,
  deleteAgamiFromTable,
  deleteAgamiMedia,
  singleDbUpsert,
  supabase,
} from './supabase.helpers'
import { uploadSpotMedia, upsertAgamiExperiences } from './upload.helpers'
import { AgamiSpot } from '../../types/supabase'

/**
 * Structured input of the v2 save: the reason this orchestrator exists.
 *
 * `upsertAgamiSpot` (v1) takes twenty flat parameters and has one consumer —
 * the form every venue uses today. Epics 07 (optional media) and 08 (one
 * content per language) change the SHAPE of this data: a flat signature cannot
 * absorb that without being rewritten, and rewriting it would touch the v1.
 * So the stable steps (uploadSpotMedia, upsertAgamiExperiences) are shared and
 * the orchestration is duplicated for the duration of the coexistence — it
 * disappears with the v1 at propagation (§10.6).
 */
export type LocalizationUpsert = {
  locale: string
  description: string | null
  /** New file to upload for this language, if any. */
  mediaFile?: File | null
  mediaFilename?: string
  /** Path already in the row, when the localization exists: routes update-vs-upload. */
  existingAudioPath?: string | null
}

export type LocalizationRemoval = {
  locale: string
  /** The file to delete AFTER the row: of the two non-atomic orders, the leftover must be the invisible one. */
  audioContentPath?: string | null
}

export type AgamiSpotV2Input = {
  userId: string
  /** The user's private library id: the manual-code library suffix logic keys on it. */
  userDefaultExpId?: string
  isDraft: boolean
  experiences: string[]
  /**
   * The additional-language tabs (08.2). The MAIN tab does not travel here: it
   * IS the spot's base columns, already carried by `spot` and `media` — which
   * is what keeps the monolingual agami the degenerate case, indistinguishable
   * from the v1's record. One save function receives every tab and decides
   * where each one writes (D6): base columns or spot_localizations.
   */
  localizations?: {
    upserts: LocalizationUpsert[]
    removals: LocalizationRemoval[]
  }
  media?: {
    file?: File | null
    filename?: string
  }
  spot: {
    /** Present when editing; a new id is generated otherwise. */
    id?: string
    name: string
    description: string
    type: number
    contentMediaType: number
    latitude?: number
    longitude?: number
    radius?: number
    deadband?: number
    manualCode?: string
    landingPageCodeId?: string | null
    /** Language of description/audio_content_path on geoxp_spots; independent of every experience. */
    baseLanguage?: string | null
    /** Current media path, when editing: the update-vs-upload routing needs it. */
    audioContentPath?: string
  }
}

export type AgamiSpotV2Result = {
  spot?: AgamiSpot
  error?: PostgrestError | Error | { message: string; statusCode?: string }
}

/**
 * Writes the additional-language diff: one row per (spot, locale), media with
 * the -{locale} path suffix, removals as ROW FIRST, FILE SECOND — the bucket
 * does not join the transaction, and of the two possible half-states a row
 * pointing at nothing is a failure the user meets, while an orphan file is
 * invisible wasted space. No deferred cleanup in this cycle (same debt as the
 * media removal 07.1 declined, noted in the story).
 *
 * Languages are independent of each other, so they run in parallel; the
 * row-then-file order that matters lives INSIDE each one.
 */
const syncSpotLocalizations = async ({
  spotId,
  contentMediaType,
  upserts,
  removals,
}: {
  spotId: string
  contentMediaType: number
  upserts: LocalizationUpsert[]
  removals: LocalizationRemoval[]
}): Promise<AgamiSpotV2Result['error'] | undefined> => {
  const removalErrors = await Promise.all(
    removals.map(async (removal) => {
      const { error } = await supabase
        .from(SPOT_LOCALIZATIONS_TABLE)
        .delete()
        .eq('spot_id', spotId)
        .eq('locale', removal.locale)
      if (error) return error

      if (removal.audioContentPath) {
        await deleteAgamiMedia({ audioPath: removal.audioContentPath, contentMediaType })
      }
      return undefined
    })
  )

  const removalError = removalErrors.find(Boolean)
  if (removalError) return removalError

  const upsertErrors = await Promise.all(
    upserts.map(async (upsert) => {
      let audioPath = upsert.existingAudioPath ?? null
      let audioFilename: string | null | undefined

      if (upsert.mediaFile) {
        const { audioPath: uploadedPath, uploadError } = await uploadSpotMedia({
          isUpdate: Boolean(upsert.existingAudioPath),
          audioContentPath: upsert.existingAudioPath ?? undefined,
          mediaFile: upsert.mediaFile,
          spotId,
          mediaType: contentMediaType,
          locale: upsert.locale,
        })
        if (uploadError) return uploadError
        audioPath = uploadedPath ?? audioPath
        audioFilename = upsert.mediaFilename
      }

      const { error } = await dbUpsert({
        table: SPOT_LOCALIZATIONS_TABLE,
        upsertData: [
          {
            spot_id: spotId,
            locale: upsert.locale,
            description: upsert.description,
            audio_content_path: audioPath,
            ...(audioFilename && { audio_filename: audioFilename }),
          },
        ],
        upsertOptions: { onConflict: 'spot_id,locale', defaultToNull: false },
      })
      return error ?? undefined
    })
  )

  return upsertErrors.find(Boolean) ?? undefined
}

/**
 * The v2 save: same three steps and same rollback semantics as the v1 —
 * upload the media, upsert the spot, reconcile the experience relations —
 * returning a promise instead of driving callbacks.
 *
 * Parity is the contract (06.1): every column written here must match what the
 * v1 writes for the same input, because a divergence would not show on screen —
 * it would show up weeks later in the config of an experience in production.
 * The comparison test of 06.2 is what enforces this.
 */
export const upsertAgamiSpotV2 = async ({
  userId,
  userDefaultExpId,
  isDraft,
  experiences,
  media,
  spot,
  localizations,
}: AgamiSpotV2Input): Promise<AgamiSpotV2Result> => {
  const isUpdate = !!spot.id
  const spotId = spot.id ?? uuidv4()

  // 1. The media, WHEN there is one (shared step). With no file the upload is
  // skipped entirely and the four audio columns stay NULL — undefined fields
  // are dropped from the upsert payload, never sent as '' (an empty path is
  // exactly what used to make createSignedUrl fail in 07.3).
  const { audioPath, uploadError } = await uploadSpotMedia({
    isUpdate,
    audioContentPath: spot.audioContentPath,
    mediaFile: media?.file,
    spotId,
    mediaType: spot.contentMediaType,
  })

  if (uploadError) {
    return { error: uploadError }
  }

  // 2. The spot row. Nullish fields are written as NULL exactly like the v1
  // does, so an edit that clears a value clears the column too.
  const { data: spotData, error: spotError } = await singleDbUpsert({
    table: GEOXP_SPOTS_TABLE,
    upsertData: {
      id: spotId,
      user_id: userId,
      name: spot.name,
      description: spot.description,
      type: spot.type,
      is_draft: isDraft,
      content_media_type: spot.contentMediaType,
      audio_filename: media?.filename,
      audio_content_path: audioPath,
      latitude: spot.latitude ?? null,
      longitude: spot.longitude ?? null,
      radius: spot.radius ?? null,
      deadband: spot.deadband ?? null,
      // Only manual spots (type=2) can carry a landing page — same guard as the
      // v1, so editing a geo spot never keeps a stale association around.
      landing_page_code_id: spot.type === 2 ? (spot.landingPageCodeId ?? null) : null,
      base_language: spot.baseLanguage ?? null,
    },
  })

  if (spotError || !spotData) {
    if (audioPath) {
      await deleteAgamiMedia({ audioPath, contentMediaType: spot.contentMediaType })
    }
    return { error: (spotError as PostgrestError) ?? new Error('spot upsert returned no row') }
  }

  const savedSpot = spotData as unknown as AgamiSpot

  // 3. The relations diff (shared step): removed ones canceled, new ones
  // created, library suffix handled inside.
  const { error: relationsError } = await upsertAgamiExperiences({
    spot_id: savedSpot.id,
    newExperiences: experiences,
    type: spot.type,
    manual_code: spot.manualCode,
    userDefaultExpId,
  })

  if (relationsError) {
    // Same rollback the v1 performs: a spot without its relations must not
    // survive the failure, nor its media.
    if (!isUpdate) {
      await deleteAgamiFromTable(savedSpot.id)
    }
    if (audioPath) {
      await deleteAgamiMedia({ audioPath, contentMediaType: spot.contentMediaType })
    }
    return { error: relationsError as PostgrestError }
  }

  // 4. The additional languages (08.2), when the editor touched them.
  if (localizations) {
    const localizationsError = await syncSpotLocalizations({
      spotId: savedSpot.id,
      contentMediaType: spot.contentMediaType,
      ...localizations,
    })
    if (localizationsError) {
      // The base spot is saved and consistent; the localization rows are
      // idempotent upserts — retrying the save is safe and completes the diff.
      return { error: localizationsError }
    }
  }

  return { spot: savedSpot }
}
